/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Icon, SortableTableColumnHeader, Button } from 'sarsaparilla';

import * as pActions from '../actions/permit';
import * as types from '../constants';

const propTypes = {
    cooperators: PropTypes.array,
    selectedCooperatorId: PropTypes.string,
    isSelectable: PropTypes.bool,
    onSelect: PropTypes.func,
    dispatch: PropTypes.func,
};

const defaultProps = {
    cooperators: [],
    isSelectable: false,
};

export class CooperatorsList extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            searchText: '',
            sort: {
                column: null,
                isSorted: false,
                isAsc: true,
            },
            selectedCooperatorId: props.selectedCooperatorId,
            expandedCooperatorIds: [],
            cooperators: props.cooperators,
            sortedCooperators: this.getSortedFilteredCooperators(props.cooperators, null),
        };
        this.onNameSortToggle = this.onNameSortToggle.bind(this);
        this.onDistrictSortToggle = this.onDistrictSortToggle.bind(this);
        this.onSearchInputChange = this.onSearchInputChange.bind(this);
    }

    componentDidMount() {
        this.refresh();
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (
            JSON.stringify(nextProps.cooperators) !==
            JSON.stringify(this.props.cooperators)
        ) {
            this.setState({
                cooperators: nextProps.cooperators,
                sortedCooperators: this.getSortedFilteredCooperators(
                    nextProps.cooperators,
                    this.state.searchText
                ),
            });
        }
    }

    onColumnSortToggle(columnName, isSorted, isAsc) {
        this.setState(
            {
                sort: {
                    column: columnName,
                    isSorted,
                    isAsc,
                },
            },
            () => {
                this.setState({
                    sortedCooperators: this.getSortedFilteredCooperators(
                        this.state.cooperators,
                        this.state.searchText
                    ),
                });
            }
        );
    }

    onNameSortToggle(isSorted, isAsc) {
        this.onColumnSortToggle('name', isSorted, isAsc);
    }

    onDistrictSortToggle(isSorted, isAsc) {
        this.onColumnSortToggle('district', isSorted, isAsc);
    }

    onSearchInputChange(event) {
        const value = event.target.value;
        this.setState(
            {
                searchText: value,
            },
            () => {
                this.setState({
                    sortedCooperators: this.getSortedFilteredCooperators(
                        this.state.cooperators,
                        value
                    ),
                });
            }
        );
    }

    onToggleCooperatorDetails(cooperatorId) {
        const expandedCooperatorIds = this.state.expandedCooperatorIds;
        const index = expandedCooperatorIds.indexOf(cooperatorId);

        if (index !== -1) {
            expandedCooperatorIds.splice(index, 1);
        } else {
            expandedCooperatorIds.push(cooperatorId);
        }
        this.setState({
            expandedCooperatorIds,
        });
    }

    getSortedFilteredCooperators(coops, searchText) {
        let ret = [];
        if (!coops) {
            return ret;
        }
        ret = coops.slice(0);

        const supervisorOfficeId = '233396-1';
        const offices = [
            '233396-1',
            '233396-4',
            '233396-2',
            '233396-3',
            '233396-5',
            '233396-6',
        ];

        if (searchText && searchText !== '') {
            const lowerCaseSearchText = searchText.toLowerCase();

            ret = ret
                .filter((item) => {
                    const text = `${item.name}  ${item.district} ${item.phone} ${item.address1} ${item.address2} ${item.city} ${item.state_code} ${item.website_url} ${item.email} ${item.primary_contact_name}`;
                    return text.toLowerCase().includes(lowerCaseSearchText);
                })
                .sort((item1, item2) => {
                    // make sure supervisor office is always on top
                    if (item1.cooperator_id === supervisorOfficeId) {
                        return -1;
                    }
                    if (item2.cooperator_id === supervisorOfficeId) {
                        return 1;
                    }
                    // sort to make sure usfs offices are on top
                    if (offices.includes(item1.cooperator_id)) {
                        return -1;
                    }
                    if (offices.includes(item2.cooperator_id)) {
                        return 1;
                    }
                    // sort to make sure name matches are on top
                    const nameMatched1 = item1.name
                        ?.toLowerCase()
                        .includes(lowerCaseSearchText);
                    const nameMatched2 = item2.name
                        ?.toLowerCase()
                        .includes(lowerCaseSearchText);
                    if (nameMatched1 && !nameMatched2) {
                        return 1;
                    }
                    if (nameMatched2 && !nameMatched1) {
                        return -1;
                    }
                    const districtMatched1 = item1.district
                        ?.toLowerCase()
                        .includes(lowerCaseSearchText);
                    const districtMatched2 = item2.district
                        ?.toLowerCase()
                        .includes(lowerCaseSearchText);
                    if (districtMatched1 && !districtMatched2) {
                        return 1;
                    }
                    if (districtMatched2 && !districtMatched1) {
                        return -1;
                    }
                    return 0;
                });
        }
        // sort alphabeticall if enabled
        if (this.state && this.state.sort.isSorted) {
            ret.sort((a, b) => {
                let o1 = a[this.state.sort.column];
                let o2 = b[this.state.sort.column];
                if (!this.state.sort.isAsc) {
                    o1 = b[this.state.sort.column];
                    o2 = a[this.state.sort.column];
                }
                if (o1 > o2) {
                    return 1;
                }
                if (o1 < o2) {
                    return -1;
                }
                return 0;
            });
        } else {
            // sort cooperators randomly/shuffle, if sorting is enabled
            for (let i = ret.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [ret[i], ret[j]] = [ret[j], ret[i]];
            }
            ret.sort((item1, item2) => {
                // make sure supervisor office is always on top
                if (item1.cooperator_id === supervisorOfficeId) {
                    return -1;
                }
                if (item2.cooperator_id === supervisorOfficeId) {
                    return 1;
                }
                // sort to make sure usfs offices are on top
                if (offices.includes(item1.cooperator_id)) {
                    return -1;
                }
                if (offices.includes(item2.cooperator_id)) {
                    return 1;
                }
                return 0;
            });
            if (this.props.selectedCooperatorId) {
                // move selected record to the top
                for (let i = 0; i < ret.length; i++) {
                    if (ret[i].cooperator_id === this.props.selectedCooperatorId) {
                        const first = ret[0];
                        ret[0] = ret[i];
                        ret[i] = first;
                        break;
                    }
                }
            }
        }
        return ret;
    }

    refresh() {
        this.props.dispatch(pActions.fetchCooperators(types.BOUNDARY_WATERS_PERMIT_ID));
    }

    handleOnSelect(cooperator) {
        this.setState({
            selectedCooperatorId: cooperator.cooperator_id,
        });
        if (this.props.onSelect) {
            this.props.onSelect(cooperator);
        }
    }

    renderCooperators() {
        const cooperatorRows = [];
        this.state.sortedCooperators.forEach((coop) => {
            let websiteUrl = coop.website_url;
            if (websiteUrl && websiteUrl !== '') {
                if (!websiteUrl.toLowerCase().includes('http')) {
                    websiteUrl = 'http://'.concat(websiteUrl);
                }
            }

            const cooperatorRow = (
                <tr key={coop.cooperator_id} className="summary-row">
                    {this.props.isSelectable && (
                        <td>
                            <label
                                htmlFor={`sel_${coop.cooperator_id}`}
                                className="rec-label-radio"
                            >
                                <span className="rec-sr-only">
                                    Select cooperator {coop.name}
                                </span>
                                <input
                                    className="rec-input-hide"
                                    type="radio"
                                    id={`sel_${coop.cooperator_id}`}
                                    name={`cooperator-radio-${coop.cooperator_id}`}
                                    placeholder={coop.name}
                                    checked={
                                        this.state.selectedCooperatorId ===
                                        coop.cooperator_id
                                    }
                                    onChange={this.handleOnSelect.bind(this, coop)}
                                />
                                <span className="rec-input-radio" />
                            </label>
                        </td>
                    )}
                    <td>
                        <Button
                            className="name"
                            size="sm"
                            appearance="subtle"
                            onClick={this.onToggleCooperatorDetails.bind(
                                this,
                                coop.cooperator_id
                            )}
                            iconAfterElement={
                                this.state.expandedCooperatorIds.indexOf(
                                    coop.cooperator_id
                                ) >= 0 ? (
                                    <Icon iconName="chevron-up" />
                                ) : (
                                    <Icon iconName="chevron-down" />
                                )
                            }
                            screenReaderTextBefore={
                                this.state.expandedCooperatorIds.indexOf(
                                    coop.cooperator_id
                                ) >= 0
                                    ? 'Hide information for '
                                    : 'Display more information for '
                            }
                        >
                            {coop.name}
                        </Button>
                    </td>
                    <td>{coop.district}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{coop.phone}</td>
                    <td>
                        {coop.city}, {coop.state_code}
                    </td>
                </tr>
            );
            cooperatorRows.push(cooperatorRow);

            if (this.state.expandedCooperatorIds.indexOf(coop.cooperator_id) >= 0) {
                const colSpan = this.props.isSelectable ? 5 : 4;
                const cooperatorDetailsRow = (
                    <tr key={`${coop.cooperator_id}_details`} className="details-row">
                        <td />
                        <td colSpan={colSpan}>
                            {coop.city && (
                                <div className="details-item">
                                    <span
                                        aria-hidden="true"
                                        className="details-icon left-icon"
                                    >
                                        <Icon iconName="mall" />
                                    </span>
                                    {coop.address1} {coop.address2}, {coop.city},{' '}
                                    {coop.state_code} {coop.zip_code}
                                </div>
                            )}
                            {coop.website_url && (
                                <div className="details-item">
                                    <span
                                        aria-hidden="true"
                                        className="details-icon left-icon"
                                    >
                                        <Icon iconName="launch" />
                                    </span>
                                    <a
                                        href={websiteUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {coop.website_url}
                                    </a>
                                </div>
                            )}
                            {coop.email && (
                                <div className="details-item">
                                    <span
                                        aria-hidden="true"
                                        className="details-icon left-icon"
                                    >
                                        <Icon iconName="email" />
                                    </span>
                                    {coop.email}
                                </div>
                            )}
                            {coop.phone && (
                                <div className="details-item">
                                    <span
                                        aria-hidden="true"
                                        className="details-icon left-icon"
                                    >
                                        <Icon iconName="phone" />
                                    </span>
                                    {coop.phone}
                                </div>
                            )}
                            {coop.hours && (
                                <div className="details-item schedule">
                                    <span
                                        aria-hidden="true"
                                        className="details-icon left-icon"
                                    >
                                        <Icon iconName="clock" />
                                    </span>
                                    <pre>{coop.hours}</pre>
                                </div>
                            )}
                            {coop.driving_directions && (
                                <div className="details-item driving-directions">
                                    <span
                                        aria-hidden="true"
                                        className="details-icon left-icon"
                                    >
                                        <Icon iconName="car" />
                                    </span>
                                    <pre>{coop.driving_directions}</pre>
                                </div>
                            )}
                        </td>
                    </tr>
                );
                cooperatorRows.push(cooperatorDetailsRow);
            }
        });
        return cooperatorRows;
    }

    render() {
        const cooperatorRows = this.renderCooperators();
        return (
            <div className="cooperators-list">
                <label htmlFor="search-cooperators-input" className="rec-sr-only">
                    Search by issue station, area...
                </label>
                <input
                    id="search-cooperators-input"
                    className="search-input"
                    placeholder="Search by issue station, area..."
                    onChange={this.onSearchInputChange}
                    value={this.state.searchText}
                />
                <div className="cooperators-list-table-wrapper">
                    <table>
                        <thead>
                            <tr>
                                {this.props.isSelectable && <th scope="col" />}
                                <SortableTableColumnHeader
                                    name="ISSUER"
                                    isSorted={
                                        this.state.sort.column === 'name'
                                            ? this.state.sort.isSorted
                                            : false
                                    }
                                    isAsc={
                                        this.state.sort.column === 'name'
                                            ? this.state.sort.isAsc
                                            : false
                                    }
                                    onSortToggle={this.onNameSortToggle}
                                />
                                <SortableTableColumnHeader
                                    name="AREA"
                                    isSorted={
                                        this.state.sort.column === 'district'
                                            ? this.state.sort.isSorted
                                            : false
                                    }
                                    isAsc={
                                        this.state.sort.column === 'district'
                                            ? this.state.sort.isAsc
                                            : false
                                    }
                                    onSortToggle={this.onDistrictSortToggle}
                                />
                                <th scope="col">PHONE</th>
                                <th scope="col">LOCATION</th>
                            </tr>
                        </thead>
                        <tbody>{cooperatorRows}</tbody>
                    </table>
                </div>
            </div>
        );
    }
}

CooperatorsList.displayName = 'CooperatorsList';
CooperatorsList.propTypes = propTypes;
CooperatorsList.defaultProps = defaultProps;

function select(state) {
    return {
        cooperators: state.boundarywaters.permit.cooperators,
    };
}

export default connect(select)(CooperatorsList);
